/**
 * polylineDrawnBand — "is this polyline drawn as a BAND, and if so which loops,
 * in which plane, and is that band FILLED?" — the one place those four answers
 * are computed for every consumer that has to agree with the canvas.
 *
 * # Why it exists
 *
 * Before this module the same three-line gate — `widths` present, non-empty,
 * `hasPolylineWidth`, then `widePolylineBandLoops(points, closed, bulges,
 * widths, extrusion, bandFrame)` — was written out separately in
 * `batchedLayer.ts` (the render stream) and `raycast/windowSelect.ts` (window /
 * crossing select), and was MISSING entirely from `raycast/distance.ts` (the
 * pointer pick, which still measured the centreline). Three copies of a rule,
 * one of them absent, is exactly the shape @codex PR #785 has been closing
 * round after round, so the gate is a function now and the pick joins the other
 * two by calling it (CLAUDE.md ПРИОРИТЕТ #2.1 — one truth, several derived
 * consumers).
 *
 * NOTHING about the band's geometry is decided here. Which widths count as
 * width, the per-vertex 40/41 indexing, the bulged-span subdivision, the taper,
 * the mitred joints, the square caps and WHICH FRAME the transverse offset is
 * built in are all `polylineWidthBand.ts::widePolylineBandLoops` — the SAME
 * call the vector plot makes. This module only asks it the question and
 * classifies the answer.
 *
 * # FILLMODE (Autodesk system-variable reference + ezdxf)
 *
 * `FILLMODE` "specifies whether hatches and fills, 2D solids, and wide
 * polylines are filled in": type Integer, INITIAL VALUE **1**, saved in the
 * drawing. `ezdxf`'s `sections/headervars.py` carries the same var at the same
 * group code with the same default (`$FILLMODE`, code 70, `default=1`,
 * DXF12-2018), and its renderer acts on it: `addons/drawing/frontend.py`, on
 * `entity.has_width`, builds a `TraceBuilder`, calls `draw_filled_polygon` with
 * a SOLID `Filling()` for every band polygon and RETURNS — the centreline is
 * never stroked and the silhouette is never outlined.
 *
 * So a width-carrying polyline has exactly TWO appearances, and the drawing's
 * own `$FILLMODE` picks between them:
 *
 *   FILLMODE 1 (the default) — the band is a SOLID region: the renderer adds
 *     the triangulated fill mesh (`scene/widePolylineFillMesh.ts`).
 *   FILLMODE 0 — the band is an OUTLINE and no mesh is emitted.
 *
 * The SILHOUETTE is stroked either way. The fill is additive, not a
 * replacement, because on this canvas the outline carries two things a plot
 * does not need: the per-instance SELECTION byte (a LINE-bucket slot — an
 * entity drawn only as a mesh can never highlight) and a minimum on-screen
 * width (triangles have no rasterisation floor, so a sub-pixel band would thin
 * out or vanish where a lineweight stroke is always at least a pixel). The
 * mesh is drawn UNDER the line buckets so the outline always wins. The plot,
 * which has neither concern, stays fill-only
 * (`print/pdf/converters/widePolylineBand.ts`, `stroke: false`).
 *
 * `RenderableEntity.fillmode` is the projection of the typed document field
 * `DrawingDocument.fillmode` (`drawing-model` `model.ts`, imported from the
 * `$FILLMODE` HEADER var by `dxf-io` `headerDocumentVars.ts`). It is ABSENT on
 * the overwhelmingly common entity and absent MEANS 1, mirroring the HEADER
 * rule that an absent `$FILLMODE` is the initial value — so only a drawing that
 * actually says `$FILLMODE 0` pays for the field.
 */

import {
  hasPolylineWidth,
  pointInPolygon,
  type FilledRegionLoop,
  type Point2D,
  type PolylineWidth,
} from "@dxf-ai-editor/drawing-model";

import type { PolylineBandFrame, Vec3 } from "../../types";

import { widePolylineBandLoops } from "./polylineWidthBand";
import {
  linearPolylinePlane,
  polylinePlane,
  type PolylinePlane,
} from "./polylinePlane";

/**
 * The `polyline` {@link SceneGeometry} fields the band construction reads.
 * Structural rather than the full union so the plot-side and test-side callers
 * that hold a bare geometry literal can pass it without a cast.
 */
export interface DrawnBandGeometry {
  points: Vec3[];
  closed: boolean;
  bulges?: number[];
  /** Per-vertex start/end widths from DXF group codes 40 / 41. */
  widths?: PolylineWidth[];
  /** OCS extrusion normal (DXF 210) of the plane the widths are authored in. */
  extrusion?: Vec3;
  /** Non-similarity block-child frame; SUPERSEDES `extrusion` when present. */
  bandFrame?: PolylineBandFrame;
}

/**
 * `true` when this polyline is DRAWN as a band at all — the cheap half of
 * {@link drawnPolylineBandLoops}, answered from the width list alone.
 *
 * Split out because two callers need the ANSWER without the band: the relift
 * fan-out (`sceneBulkLoadHelpers.ts::carriesDirectFillMeshes`) only has to know
 * whether a fill mesh will exist, and asking that with a full silhouette
 * construction would be pure waste on a path that walks every newly-visible
 * entity. `hasPolylineWidth` is `drawing-model`'s one rule for "does this count
 * as width at all" — a list of zeros does not, and such a polyline is a stroked
 * centreline.
 */
export function polylineDeclaresBandWidth(
  geometry: DrawnBandGeometry,
): boolean {
  const widths = geometry.widths;
  if (!widths || widths.length === 0) return false;
  return hasPolylineWidth(widths);
}

/**
 * The closed loops of the BAND a width-carrying polyline is drawn as, or `null`
 * when the geometry declares no width at all (the caller then falls back to the
 * centreline, exactly as before).
 *
 * An OPEN run yields ONE loop (the silhouette, square caps included); a CLOSED
 * run yields TWO (the two edges of the annulus, in no guaranteed outer-first
 * order — see {@link bandRingRoles}).
 */
export function drawnPolylineBandLoops(
  geometry: DrawnBandGeometry,
): Vec3[][] | null {
  const widths = geometry.widths;
  // The `widths` re-test is the TYPE narrowing, not a second rule: the RULE is
  // `polylineDeclaresBandWidth`, which the batcher also calls on its own.
  if (!widths || !polylineDeclaresBandWidth(geometry)) return null;
  const loops = widePolylineBandLoops(
    geometry.points,
    geometry.closed,
    geometry.bulges,
    widths,
    geometry.extrusion,
    geometry.bandFrame,
  );
  return loops.length > 0 ? loops : null;
}

/**
 * The plane the band was CONSTRUCTED in, or `null` when that is the WCS XY
 * plane (every LWPOLYLINE the canvas draws today, every untilted POLYLINE, and
 * every block child under a similarity INSERT).
 *
 * Same precedence `widePolylineBandLoops` applies internally — `bandFrame`
 * supersedes `extrusion`, because it already composes the child's own OCS basis
 * and applying both would lift the same plane twice. Callers that need to do
 * 2D work ON the finished loops (triangulating the fill; deciding which ring of
 * a closed band is the outer one) project through this, so they work in the
 * frame the band is actually planar in rather than in a WCS XY shadow that a
 * tilted band can collapse to zero area in.
 */
export function drawnPolylineBandPlane(
  geometry: DrawnBandGeometry,
): PolylinePlane | null {
  return geometry.bandFrame
    ? linearPolylinePlane(geometry.bandFrame)
    : polylinePlane(geometry.extrusion);
}

/**
 * `true` when the drawing's `$FILLMODE` paints solid — i.e. anything but an
 * explicit 0. An ABSENT value is the Autodesk documented INITIAL VALUE 1 (see
 * the module header), which is why this is not a plain truthiness test.
 */
export function fillModePaintsSolid(fillmode: number | undefined): boolean {
  return fillmode !== 0;
}

/**
 * A band's loops projected into the frame they were CONSTRUCTED in, together
 * with the frame itself so the caller can lift results back out.
 *
 * Every consumer that does 2D work ON the finished loops needs this: a band in
 * a tilted OCS plane has a squashed WCS-XY shadow — for extrusion (0,1,0) one
 * of exactly ZERO area — so ring containment and earcut triangulation must both
 * run in the band's own plane or they answer about a shadow instead of the
 * band. `plane === null` (the default extrusion, every LWPOLYLINE the canvas
 * draws today) returns the input arrays THEMSELVES, not copies, so the common
 * case allocates nothing.
 */
export function bandLoopsInPlane(
  loops: readonly (readonly Vec3[])[],
  geometry: DrawnBandGeometry,
): { local: readonly (readonly Vec3[])[]; plane: PolylinePlane | null } {
  const plane = drawnPolylineBandPlane(geometry);
  if (!plane) return { local: loops, plane: null };
  return {
    local: loops.map((loop) => loop.map((p) => plane.toLocal(p))),
    plane,
  };
}

/**
 * Which loop of a band is the OUTER edge, and which are the holes it punches —
 * as INDICES into the loop array, so the caller applies the roles to whichever
 * copy of the loops it needs (the plane-local ones for earcut, the WORLD ones
 * for a top-view pick).
 */
export interface BandRingRoles {
  /** Index of the outer edge. 0 for the single loop of an open run. */
  exterior: number;
  /** Indices of the inner edges — a closed band's annulus hole. */
  holes: number[];
}

/** Sample stride so the containment vote stays O(1) per ring pair. */
const RING_VOTE_SAMPLES = 5;

/** How many of `ring`'s sampled vertices fall inside `other`. */
function verticesInside(
  ring: readonly Vec3[],
  other: readonly Vec3[],
): number {
  if (ring.length === 0 || other.length < 3) return 0;
  const step = Math.max(1, Math.floor(ring.length / RING_VOTE_SAMPLES));
  let inside = 0;
  for (let i = 0; i < ring.length; i += step) {
    if (pointInPolygon(ring[i]!, other)) inside += 1;
  }
  return inside;
}

/**
 * Split a band's loops into exterior + holes. `loops` MUST be the PLANE-LOCAL
 * copy ({@link bandLoopsInPlane}) — the containment test is 2D.
 *
 * WHY A CONTAINMENT VOTE AND NOT A WINDING RULE: `widePolylineOutlineLoops`
 * returns a closed run's two edges as `[left, right]` — the +1 and -1 transverse
 * offsets, each walked in the POLYLINE's own direction. Which of the two is the
 * OUTER edge is therefore the polyline's winding, which the DXF does not
 * constrain: a clockwise closed LWPOLYLINE puts the left offset INSIDE. Neither
 * the band builder nor the DXF promises an orientation, so the ring roles are
 * measured here rather than assumed — `pointInPolygon` is `drawing-model`'s own
 * one-truth predicate (the same one the HATCH pick uses), sampled on both sides
 * so a ring that degenerately touches the other cannot flip the answer on a
 * single vertex.
 *
 * The plot needs none of this: it fills NONZERO with every ring after the first
 * reversed, a rule that is winding-agnostic by construction. earcut is not —
 * it subtracts a hole from a NAMED exterior — so the canvas has to name them.
 */
export function bandRingRoles(
  loops: readonly (readonly Vec3[])[],
): BandRingRoles {
  if (loops.length <= 1) return { exterior: 0, holes: [] };
  // The outer edge is the one no sibling encloses. For the only shape that
  // reaches here (one or two rings of a single band) this is the two-ring vote
  // written generally.
  let exterior = 0;
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < loops.length; i += 1) {
    let contained = 0;
    for (let j = 0; j < loops.length; j += 1) {
      if (i !== j) contained += verticesInside(loops[i]!, loops[j]!);
    }
    if (contained < best) {
      best = contained;
      exterior = i;
    }
  }
  const holes: number[] = [];
  for (let i = 0; i < loops.length; i += 1) if (i !== exterior) holes.push(i);
  return { exterior, holes };
}

/**
 * The band as an EXTERIOR-MINUS-HOLES filled region, ready for `drawing-model`'s
 * one-truth {@link isPointInFilledRegion} — "what does `$FILLMODE` 1 actually
 * paint here?", answered once for every consumer that has to agree with the
 * fill mesh.
 *
 * # Why it is a function and not two lines at each call site
 *
 * Round 11 gave the POINTER PICK this reading (`raycast/distance.ts`) while
 * CROSSING SELECT kept the edge-only one, so a right-to-left rectangle lying
 * wholly inside a wide ribbon picked at distance 0 and selected nothing
 * (@codex PR #785 round 12, F1). Two consumers of one shape is exactly the
 * split this module exists to prevent, so the roles-plus-region assembly lives
 * here and both call it — a SECOND point-in-band test would be a parallel truth
 * (CLAUDE.md ПРИОРИТЕТ #2.1) and a `dup:check` clone besides.
 *
 * # The two spaces, and why they are separate arguments
 *
 * `loops` are the loops to TEST IN — the WORLD loops for the pointer (whose
 * position is a top-view world XY), or those same loops mapped into PAPER space
 * for a twisted viewport's window select (`WindowRefinement.mapPoint`). The
 * ring ROLES, though, are decided in the band's OWN plane ({@link
 * bandLoopsInPlane}), where a tilted band is not squashed to a zero-area
 * shadow — they are an index assignment, so they transfer to any space the same
 * loops are expressed in. `project` maps a world loop vertex into the test
 * space; omitted, the loops are used as they are (a `Vec3` IS a `Point2D` with
 * a `z` the 2D predicate ignores) and the common case allocates no vertices.
 */
export function drawnBandFilledRegion(
  loops: readonly (readonly Vec3[])[],
  geometry: DrawnBandGeometry,
  project?: ((p: Vec3) => Point2D) | null,
): FilledRegionLoop[] {
  const { local } = bandLoopsInPlane(loops, geometry);
  const roles = bandRingRoles(local);
  return loops.map((loop, i) => ({
    vertices: project ? loop.map(project) : loop,
    isHole: i !== roles.exterior,
  }));
}
